/**
 * LiveStream client for AppSync Events.
 *
 * Manages a WebSocket connection to an AWS AppSync Event API and publishes temperature/roast data
 * in real-time. Uses the AppSync Events WebSocket protocol with Lambda authorization, passing the
 * user's existing RoastLog JWT as the authorization token.
 *
 * The channel structure is:
 *   /roaster/<roasterId>
 *
 * This module is designed to be called from GlobalState.setTemps() on every tick (~1s).
 */
import logger from '$lib/logger';
import type { LiveStreamConfig, LiveStreamEvent } from './types';

// WebSocket close codes
const NORMAL_CLOSURE = 1000;

// Reconnect settings
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

export class LiveStream {
  #socket: WebSocket | null = null;
  #state: ConnectionState = 'disconnected';
  #config: LiveStreamConfig | null = null;
  #token: string | null = null;
  #roasterId: string | null = null;
  #reconnectAttempts = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #keepAliveTimer: ReturnType<typeof setTimeout> | null = null;
  #connectionTimeoutMs = 300_000; // default 5 min, updated from connection_ack
  #messageCounter = 0;
  #enabled = false;

  /**
   * Initialize the live stream with AppSync Event API configuration.
   * This should be called once at app startup if the env vars are present.
   */
  configure(config: LiveStreamConfig) {
    this.#config = config;
    logger.info('LiveStream configured', {
      httpEndpoint: config.httpEndpoint,
      realtimeEndpoint: config.realtimeEndpoint
    });
  }

  get isConfigured(): boolean {
    return this.#config !== null;
  }

  get isConnected(): boolean {
    return this.#state === 'connected';
  }

  get isEnabled(): boolean {
    return this.#enabled;
  }

  /**
   * Start streaming for a given roaster. Opens the WebSocket connection and begins publishing.
   */
  connect(roasterId: string, token: string) {
    if (!this.#config) {
      logger.warn('LiveStream not configured, skipping connect');
      return;
    }

    if (this.#state !== 'disconnected') {
      // Already connected or connecting — if roaster changed, reconnect
      if (this.#roasterId !== roasterId) {
        this.disconnect();
      } else {
        return;
      }
    }

    this.#roasterId = roasterId;
    this.#token = token;
    this.#enabled = true;
    this.#reconnectAttempts = 0;
    this.#openSocket();
  }

  /**
   * Disconnect and stop streaming.
   */
  disconnect() {
    this.#enabled = false;
    this.#cleanup();
    logger.info('LiveStream disconnected');
  }

  /**
   * Publish a temperature reading to the live stream channel.
   * This is a fire-and-forget operation — if the socket isn't connected, the event is silently
   * dropped. This is intentional: we don't want to queue up stale temperature data.
   */
  publish(event: LiveStreamEvent) {
    if (!this.#enabled || this.#state !== 'connected' || !this.#socket || !this.#roasterId) {
      return;
    }

    const channel = `/roaster/${this.#roasterId}`;
    const id = `pub-${++this.#messageCounter}`;

    const message = {
      id,
      type: 'publish',
      channel,
      events: [JSON.stringify(event)],
      authorization: {
        Authorization: this.#token,
        host: this.#getHttpHost()
      }
    };

    try {
      this.#socket.send(JSON.stringify(message));
    } catch (err) {
      logger.warn('LiveStream publish failed', { error: String(err) });
    }
  }

  // --- Private methods ---

  #openSocket() {
    if (!this.#config || !this.#token) return;

    this.#state = 'connecting';

    const authorization = {
      Authorization: this.#token,
      host: this.#getHttpHost()
    };

    const headerProtocol = this.#encodeAuthProtocol(authorization);

    try {
      this.#socket = new WebSocket(`${this.#config.realtimeEndpoint}/event/realtime`, [
        'aws-appsync-event-ws',
        headerProtocol
      ]);

      this.#socket.onopen = this.#onOpen.bind(this);
      this.#socket.onmessage = this.#onMessage.bind(this);
      this.#socket.onerror = this.#onError.bind(this);
      this.#socket.onclose = this.#onClose.bind(this);
    } catch (err) {
      logger.error('LiveStream failed to create WebSocket', { error: String(err) });
      this.#state = 'disconnected';
      this.#scheduleReconnect();
    }
  }

  #onOpen() {
    logger.info('LiveStream WebSocket opened');
    // Send connection_init
    this.#socket?.send(JSON.stringify({ type: 'connection_init' }));
  }

  #onMessage(event: MessageEvent) {
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(event.data as string);
    } catch {
      return;
    }

    switch (data.type) {
      case 'connection_ack':
        this.#state = 'connected';
        this.#reconnectAttempts = 0;
        if (typeof data.connectionTimeoutMs === 'number') {
          this.#connectionTimeoutMs = data.connectionTimeoutMs;
        }
        this.#resetKeepAliveTimer();
        logger.info('LiveStream connected', {
          connectionTimeoutMs: this.#connectionTimeoutMs
        });
        break;

      case 'ka':
        this.#resetKeepAliveTimer();
        break;

      case 'publish_success':
        // No action needed — fire and forget
        break;

      case 'publish_error':
        logger.warn('LiveStream publish error', { data });
        break;

      case 'connection_error':
        logger.error('LiveStream connection error', { data });
        this.#cleanup();
        this.#scheduleReconnect();
        break;

      default:
        // subscribe_success, data, etc. — not expected for publisher-only usage
        break;
    }
  }

  #onError(event: Event) {
    logger.error('LiveStream WebSocket error', { event: String(event) });
  }

  #onClose(event: CloseEvent) {
    logger.info('LiveStream WebSocket closed', {
      code: event.code,
      reason: event.reason
    });

    this.#state = 'disconnected';
    this.#clearKeepAliveTimer();

    // Only reconnect if we're still supposed to be enabled
    if (this.#enabled && event.code !== NORMAL_CLOSURE) {
      this.#scheduleReconnect();
    }
  }

  #scheduleReconnect() {
    if (!this.#enabled || this.#reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (this.#reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        logger.warn('LiveStream max reconnect attempts reached, giving up');
      }
      return;
    }

    // Exponential backoff with jitter
    const delay =
      BASE_RECONNECT_DELAY_MS * Math.pow(2, this.#reconnectAttempts) * (0.5 + Math.random() * 0.5);
    this.#reconnectAttempts++;

    logger.info('LiveStream scheduling reconnect', {
      attempt: this.#reconnectAttempts,
      delayMs: Math.round(delay)
    });

    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.#openSocket();
    }, delay);
  }

  #resetKeepAliveTimer() {
    this.#clearKeepAliveTimer();
    // If we don't receive a keep-alive within the timeout, the connection is stale
    this.#keepAliveTimer = setTimeout(() => {
      logger.warn('LiveStream keep-alive timeout, reconnecting');
      this.#cleanup();
      if (this.#enabled) {
        this.#scheduleReconnect();
      }
    }, this.#connectionTimeoutMs);
  }

  #clearKeepAliveTimer() {
    if (this.#keepAliveTimer) {
      clearTimeout(this.#keepAliveTimer);
      this.#keepAliveTimer = null;
    }
  }

  #cleanup() {
    this.#clearKeepAliveTimer();

    if (this.#reconnectTimer) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }

    if (this.#socket) {
      // Remove handlers to avoid triggering reconnect from the close event
      this.#socket.onopen = null;
      this.#socket.onmessage = null;
      this.#socket.onerror = null;
      this.#socket.onclose = null;

      if (
        this.#socket.readyState === WebSocket.OPEN ||
        this.#socket.readyState === WebSocket.CONNECTING
      ) {
        this.#socket.close(NORMAL_CLOSURE);
      }
      this.#socket = null;
    }

    this.#state = 'disconnected';
  }

  #getHttpHost(): string {
    if (!this.#config) return '';
    try {
      return new URL(this.#config.httpEndpoint).host;
    } catch {
      // Fallback: strip protocol
      return this.#config.httpEndpoint.replace(/^https?:\/\//, '');
    }
  }

  /**
   * Encode the authorization object as a base64url WebSocket subprotocol header,
   * per the AppSync Events WebSocket protocol spec.
   */
  #encodeAuthProtocol(authorization: Record<string, string | null>): string {
    const header = btoa(JSON.stringify(authorization))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return `header-${header}`;
  }
}

/** Singleton instance */
export const liveStream = new LiveStream();
